// src/modules/leads/LeadPipeline.js
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import {
  FaUser, FaPhone, FaPhoneAlt, FaMapMarkerAlt, FaCalendarAlt, FaFilter,
  FaTimes, FaGripVertical, FaEye, FaChevronLeft, FaChevronRight,
  FaSearch, FaEnvelope, FaArrowRight, FaSyncAlt
} from 'react-icons/fa';
import { getLeadsPipeline, updateLeadPipeline } from './leadService';
import DashboardLayout from '../../layouts/DashboardLayout';
import LoadingSpinner from '../../components/shared/LoadingSpinner';
import CallModal from '../../components/shared/CallModal';
import formatPhone from '../../components/shared/formatPhone';
import OriginBadge from '../../components/shared/OriginBadge';

// Pipeline stage definitions
const PIPELINE_STAGES = [
  { key: 'new', label: 'New', color: '#4FC3F7', icon: '🆕' },
  { key: 'first_contact', label: 'First Contact', color: '#81C784', icon: '📞' },
  { key: 'quote_sent', label: 'Quote Sent', color: '#FFB74D', icon: '📧' },
  { key: 'quote_opened', label: 'Quote Opened', color: '#FF8A65', icon: '👁' },
  { key: 'follow_up_1', label: '1st Follow Up', color: '#BA68C8', icon: '1️⃣' },
  { key: 'follow_up_2', label: '2nd Follow Up', color: '#9575CD', icon: '2️⃣' },
  { key: 'follow_up_3', label: '3rd Follow Up', color: '#7986CB', icon: '3️⃣' },
  { key: 'won', label: 'Won', color: '#66BB6A', icon: '🏆' },
  { key: 'lost', label: 'Lost', color: '#ef5350', icon: '❌' },
];

// ============ STYLED COMPONENTS ============

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: calc(100vh - 120px);
  overflow: hidden;
`;

const TopBar = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  background: ${p => p.theme.colors.surface};
  border-radius: ${p => p.theme.borderRadius};
  margin-bottom: 12px;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    padding: 12px;
    gap: 8px;
  }
`;

const SearchBox = styled.div`
  display: flex;
  align-items: center;
  background: ${p => p.theme.colors.background};
  border: 1px solid ${p => p.theme.colors.inputGray};
  border-radius: 8px;
  padding: 0 12px;
  flex: 1;
  min-width: 180px;

  input {
    flex: 1;
    padding: 10px 8px;
    background: transparent;
    border: none;
    color: ${p => p.theme.colors.textPrimary};
    font-size: 14px;
    &:focus { outline: none; }
  }

  svg { color: ${p => p.theme.colors.textSecondary}; flex-shrink: 0; }
`;

const FilterBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 16px;
  background: ${p => p.active ? p.theme.colors.primary + '22' : p.theme.colors.background};
  color: ${p => p.active ? p.theme.colors.primary : p.theme.colors.textSecondary};
  border: 1px solid ${p => p.active ? p.theme.colors.primary : p.theme.colors.inputGray};
  border-radius: 8px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
  transition: all 0.2s;
  white-space: nowrap;

  &:hover {
    border-color: ${p => p.theme.colors.primary};
    color: ${p => p.theme.colors.primary};
  }
`;

const FilterPanel = styled.div`
  display: ${p => p.open ? 'flex' : 'none'};
  gap: 12px;
  padding: 12px 16px;
  background: ${p => p.theme.colors.surface};
  border-radius: ${p => p.theme.borderRadius};
  margin-bottom: 12px;
  flex-wrap: wrap;
  align-items: center;
`;

const FilterSelect = styled.select`
  padding: 8px 12px;
  background: ${p => p.theme.colors.background};
  color: ${p => p.theme.colors.textPrimary};
  border: 1px solid ${p => p.theme.colors.inputGray};
  border-radius: 8px;
  font-size: 13px;
  cursor: pointer;
  min-width: 140px;

  &:focus { outline: none; border-color: ${p => p.theme.colors.primary}; }
`;

const FilterInput = styled.input`
  padding: 8px 12px;
  background: ${p => p.theme.colors.background};
  color: ${p => p.theme.colors.textPrimary};
  border: 1px solid ${p => p.theme.colors.inputGray};
  border-radius: 8px;
  font-size: 13px;
  min-width: 140px;

  &:focus { outline: none; border-color: ${p => p.theme.colors.primary}; }
`;

const FilterLabel = styled.span`
  font-size: 13px;
  color: ${p => p.theme.colors.textSecondary};
  font-weight: 500;
`;

const ActiveFilters = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

const FilterChip = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  background: ${p => p.theme.colors.primary}15;
  border: 1px solid ${p => p.theme.colors.primary}44;
  border-radius: 16px;
  font-size: 12px;
  color: ${p => p.theme.colors.primary};

  button {
    background: none;
    border: none;
    cursor: pointer;
    color: inherit;
    padding: 0;
    display: flex;
    font-size: 10px;
  }
`;

const StatsRow = styled.div`
  display: flex;
  gap: 8px;
  margin-left: auto;
  font-size: 13px;
  color: ${p => p.theme.colors.textSecondary};
  white-space: nowrap;

  strong { color: ${p => p.theme.colors.textPrimary}; }
`;

// ============ PIPELINE BOARD ============

const BoardContainer = styled.div`
  flex: 1;
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
  scroll-snap-type: x mandatory;
  display: flex;
  gap: 12px;
  padding: 0 4px 12px 4px;

  /* Hide scrollbar but keep scroll */
  &::-webkit-scrollbar { height: 6px; }
  &::-webkit-scrollbar-track { background: transparent; }
  &::-webkit-scrollbar-thumb {
    background: ${p => p.theme.colors.inputGray};
    border-radius: 3px;
  }

  @media (max-width: 768px) {
    scroll-snap-type: x mandatory;
    gap: 8px;
    padding: 0 2px 8px 2px;
  }
`;

const Column = styled.div`
  flex: 0 0 280px;
  max-height: 100%;
  display: flex;
  flex-direction: column;
  background: ${p => p.theme.colors.background};
  border-radius: 12px;
  border: 1px solid ${p => p.theme.colors.inputGray}33;
  scroll-snap-align: start;
  transition: box-shadow 0.2s;

  ${p => p.isDragOver && `
    box-shadow: 0 0 0 2px ${p.stageColor}88;
    background: ${p.stageColor}08;
  `}

  @media (max-width: 768px) {
    flex: 0 0 85vw;
  }
`;

const ColumnHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  border-bottom: 3px solid ${p => p.color};
  background: ${p => p.color}12;
  border-radius: 12px 12px 0 0;
  user-select: none;
`;

const ColumnTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  font-size: 14px;
  color: ${p => p.theme.colors.textPrimary};
`;

const CountBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 24px;
  height: 24px;
  padding: 0 6px;
  background: ${p => p.color};
  color: #fff;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 700;
`;

const CardList = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 8px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-height: 60px;

  &::-webkit-scrollbar { width: 4px; }
  &::-webkit-scrollbar-thumb {
    background: ${p => p.theme.colors.inputGray};
    border-radius: 2px;
  }
`;

// ============ LEAD CARD ============

const LeadCard = styled.div`
  background: ${p => p.theme.colors.surface};
  border-radius: 10px;
  padding: 12px;
  cursor: grab;
  border: 1px solid ${p => p.theme.colors.inputGray}33;
  transition: all 0.2s;
  position: relative;
  user-select: none;

  ${p => p.isDragging && `
    opacity: 0.5;
    transform: rotate(2deg);
    box-shadow: 0 8px 24px rgba(0,0,0,0.15);
  `}

  &:hover {
    box-shadow: 0 2px 8px rgba(0,0,0,0.08);
    border-color: ${p => p.stageColor}66;
  }

  &:active {
    cursor: grabbing;
  }
`;

const CardName = styled.div`
  font-weight: 600;
  font-size: 14px;
  color: ${p => p.theme.colors.textPrimary};
  margin-bottom: 6px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const CardInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: ${p => p.theme.colors.textSecondary};
  margin-bottom: 4px;

  svg { font-size: 11px; flex-shrink: 0; color: ${p => p.color || 'inherit'}; }
`;

const CardDate = styled.div`
  font-size: 11px;
  color: ${p => p.theme.colors.textSecondary};
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid ${p => p.theme.colors.inputGray}22;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const DaysInStage = styled.span`
  font-size: 11px;
  padding: 2px 8px;
  border-radius: 10px;
  font-weight: 600;
  background: ${p => {
    if (p.days <= 2) return '#66BB6A22';
    if (p.days <= 5) return '#FFB74D22';
    return '#ef535022';
  }};
  color: ${p => {
    if (p.days <= 2) return '#66BB6A';
    if (p.days <= 5) return '#FFB74D';
    return '#ef5350';
  }};
`;

const CardActions = styled.div`
  display: flex;
  gap: 4px;
`;

const CardBtn = styled.button`
  background: ${p => p.theme.colors.background};
  border: none;
  border-radius: 6px;
  padding: 6px;
  cursor: pointer;
  color: ${p => p.theme.colors.textSecondary};
  font-size: 12px;
  transition: all 0.15s;
  display: flex;
  align-items: center;

  &:hover {
    background: ${p => p.theme.colors.primary}22;
    color: ${p => p.theme.colors.primary};
  }
`;

const LeadStatusBadge = styled.span`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 600;
  background: ${p => p.color ? p.color + '22' : '#99999922'};
  color: ${p => p.color || '#999'};
  white-space: nowrap;
  max-width: 120px;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const CallBtn = styled.button`
  background: #66BB6A22;
  border: none;
  border-radius: 6px;
  padding: 6px;
  cursor: pointer;
  color: #66BB6A;
  font-size: 12px;
  transition: all 0.15s;
  display: flex;
  align-items: center;

  &:hover {
    background: #66BB6A44;
    color: #43A047;
  }
`;

// ============ MOBILE NAVIGATION ============

const MobileNav = styled.div`
  display: none;
  @media (max-width: 768px) {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 8px 16px;
    background: ${p => p.theme.colors.surface};
    border-radius: 8px;
    margin-bottom: 8px;
  }
`;

const MobileNavBtn = styled.button`
  background: none;
  border: none;
  color: ${p => p.theme.colors.primary};
  font-size: 20px;
  padding: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;

  &:disabled {
    color: ${p => p.theme.colors.inputGray};
    cursor: default;
  }
`;

const MobileNavLabel = styled.span`
  font-weight: 600;
  font-size: 14px;
  color: ${p => p.theme.colors.textPrimary};
  display: flex;
  align-items: center;
  gap: 6px;
`;

// Mobile stage quick-move dropdown
const MoveDropdown = styled.div`
  position: absolute;
  top: 100%;
  right: 0;
  z-index: 100;
  background: ${p => p.theme.colors.surface};
  border: 1px solid ${p => p.theme.colors.inputGray};
  border-radius: 10px;
  box-shadow: 0 8px 24px rgba(0,0,0,0.15);
  min-width: 200px;
  overflow: hidden;
`;

const MoveOption = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 10px 14px;
  background: ${p => p.active ? p.color + '15' : 'transparent'};
  border: none;
  cursor: pointer;
  font-size: 13px;
  color: ${p => p.theme.colors.textPrimary};
  font-weight: ${p => p.active ? '600' : '400'};
  text-align: left;
  transition: background 0.15s;

  &:hover { background: ${p => p.color}15; }
`;

const StageDot = styled.span`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: ${p => p.color};
  flex-shrink: 0;
`;

const RefreshBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  background: transparent;
  color: ${p => p.theme.colors.textSecondary};
  border: 1px solid ${p => p.theme.colors.inputGray};
  border-radius: 8px;
  cursor: pointer;
  font-size: 13px;
  transition: all 0.2s;

  &:hover {
    background: ${p => p.theme.colors.primary}11;
    color: ${p => p.theme.colors.primary};
    border-color: ${p => p.theme.colors.primary};
  }

  svg {
    transition: transform 0.3s;
  }

  &.spinning svg {
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

// ============ COMPONENT ============

const LeadPipeline = () => {
  const [pipeline, setPipeline] = useState({});
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [cityFilter, setCityFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [dragData, setDragData] = useState(null);
  const [dragOverStage, setDragOverStage] = useState(null);
  const [moveMenuLead, setMoveMenuLead] = useState(null);
  const [cities, setCities] = useState([]);
  const [mobileStageIndex, setMobileStageIndex] = useState(0);
  const [callModalOpen, setCallModalOpen] = useState(false);
  const [leadToCall, setLeadToCall] = useState(null);
  const boardRef = useRef(null);
  const navigate = useNavigate();

  const handleCallClick = (lead) => {
    setLeadToCall(lead);
    setCallModalOpen(true);
  };

  const handleCallSuccess = (callResult) => {
    console.log('Call initiated successfully:', callResult);
  };

  const fetchPipeline = useCallback(async (isRefresh = false) => {
    try {
      if (isRefresh) setRefreshing(true); else setLoading(true);
      const data = await getLeadsPipeline();
      setPipeline(data.pipeline || {});

      // Extract unique cities
      const allCities = new Set();
      Object.values(data.pipeline || {}).forEach(leads => {
        leads.forEach(l => { if (l.city) allCities.add(l.city); });
      });
      setCities([...allCities].sort());
    } catch (err) {
      console.error('Failed to load pipeline:', err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { fetchPipeline(); }, [fetchPipeline]);

  // Filter leads within each stage
  const getFilteredLeads = useCallback((stageLeads) => {
    if (!stageLeads) return [];
    return stageLeads.filter(lead => {
      // Search
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        const match = lead.name?.toLowerCase().includes(q) ||
                      lead.email?.toLowerCase().includes(q) ||
                      lead.phone?.includes(q) ||
                      lead.city?.toLowerCase().includes(q);
        if (!match) return false;
      }
      // City filter
      if (cityFilter && lead.city !== cityFilter) return false;
      // Date filter
      if (dateFrom) {
        const created = new Date(lead.createdAt);
        if (created < new Date(dateFrom)) return false;
      }
      if (dateTo) {
        const created = new Date(lead.createdAt);
        const to = new Date(dateTo);
        to.setHours(23, 59, 59);
        if (created > to) return false;
      }
      return true;
    });
  }, [searchQuery, cityFilter, dateFrom, dateTo]);

  // Drag handlers
  const handleDragStart = (e, lead, fromStage) => {
    setDragData({ lead, fromStage });
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', lead.id);
  };

  const handleDragOver = (e, stage) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverStage(stage);
  };

  const handleDragLeave = () => {
    setDragOverStage(null);
  };

  const handleDrop = async (e, toStage) => {
    e.preventDefault();
    setDragOverStage(null);

    if (!dragData || dragData.fromStage === toStage) {
      setDragData(null);
      return;
    }

    await moveLead(dragData.lead, dragData.fromStage, toStage);
    setDragData(null);
  };

  const moveLead = async (lead, fromStage, toStage) => {
    // Optimistic update
    setPipeline(prev => {
      const updated = { ...prev };
      updated[fromStage] = (updated[fromStage] || []).filter(l => l.id !== lead.id);
      const movedLead = { ...lead, pipeline_stage: toStage };
      updated[toStage] = [movedLead, ...(updated[toStage] || [])];
      return updated;
    });

    try {
      await updateLeadPipeline(lead.id, toStage);
    } catch (err) {
      console.error('Failed to move lead:', err);
      // Revert on error
      fetchPipeline(true);
    }
  };

  // Touch drag for mobile
  const handleTouchMoveToStage = (lead, currentStage, targetStage) => {
    setMoveMenuLead(null);
    moveLead(lead, currentStage, targetStage);
  };

  const getDaysInStage = (lead, stageKey) => {
    const stageDate = lead.pipeline_dates?.[stageKey];
    if (!stageDate) return 0;
    return Math.floor((Date.now() - new Date(stageDate).getTime()) / 86400000);
  };

  const formatStageDate = (lead, stageKey) => {
    const d = lead.pipeline_dates?.[stageKey];
    if (!d) return '';
    return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const totalFiltered = PIPELINE_STAGES.reduce((sum, s) =>
    sum + getFilteredLeads(pipeline[s.key]).length, 0
  );

  const hasActiveFilters = cityFilter || dateFrom || dateTo;

  if (loading) {
    return <DashboardLayout><LoadingSpinner /></DashboardLayout>;
  }

  return (
    <DashboardLayout>
      <PageContainer>
        {/* Top search & filter bar */}
        <TopBar>
          <SearchBox>
            <FaSearch />
            <input
              placeholder="Search leads..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <button onClick={() => setSearchQuery('')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit' }}>
                <FaTimes />
              </button>
            )}
          </SearchBox>

          <FilterBtn active={showFilters || hasActiveFilters} onClick={() => setShowFilters(!showFilters)}>
            <FaFilter /> Filters {hasActiveFilters && '(active)'}
          </FilterBtn>

          <RefreshBtn className={refreshing ? 'spinning' : ''} onClick={() => fetchPipeline(true)}>
            <FaSyncAlt /> Refresh
          </RefreshBtn>

          <StatsRow>
            <span><strong>{totalFiltered}</strong> leads</span>
          </StatsRow>
        </TopBar>

        {/* Filter panel */}
        <FilterPanel open={showFilters}>
          <FilterLabel>City:</FilterLabel>
          <FilterSelect value={cityFilter} onChange={e => setCityFilter(e.target.value)}>
            <option value="">All Cities</option>
            {cities.map(c => <option key={c} value={c}>{c}</option>)}
          </FilterSelect>

          <FilterLabel>From:</FilterLabel>
          <FilterInput type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} />

          <FilterLabel>To:</FilterLabel>
          <FilterInput type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} />

          {hasActiveFilters && (
            <FilterBtn onClick={() => { setCityFilter(''); setDateFrom(''); setDateTo(''); }}>
              <FaTimes /> Clear All
            </FilterBtn>
          )}
        </FilterPanel>

        {/* Active filter chips */}
        {hasActiveFilters && (
          <ActiveFilters style={{ marginBottom: 8, paddingLeft: 4 }}>
            {cityFilter && (
              <FilterChip>
                <FaMapMarkerAlt /> {cityFilter}
                <button onClick={() => setCityFilter('')}><FaTimes /></button>
              </FilterChip>
            )}
            {dateFrom && (
              <FilterChip>
                From: {dateFrom}
                <button onClick={() => setDateFrom('')}><FaTimes /></button>
              </FilterChip>
            )}
            {dateTo && (
              <FilterChip>
                To: {dateTo}
                <button onClick={() => setDateTo('')}><FaTimes /></button>
              </FilterChip>
            )}
          </ActiveFilters>
        )}

        {/* Mobile stage navigation */}
        <MobileNav>
          <MobileNavBtn
            disabled={mobileStageIndex === 0}
            onClick={() => {
              const newIdx = Math.max(0, mobileStageIndex - 1);
              setMobileStageIndex(newIdx);
              boardRef.current?.children[newIdx]?.scrollIntoView({ behavior: 'smooth', inline: 'start' });
            }}
          >
            <FaChevronLeft />
          </MobileNavBtn>
          <MobileNavLabel>
            <span>{PIPELINE_STAGES[mobileStageIndex]?.icon}</span>
            {PIPELINE_STAGES[mobileStageIndex]?.label}
            <CountBadge color={PIPELINE_STAGES[mobileStageIndex]?.color}>
              {getFilteredLeads(pipeline[PIPELINE_STAGES[mobileStageIndex]?.key]).length}
            </CountBadge>
          </MobileNavLabel>
          <MobileNavBtn
            disabled={mobileStageIndex === PIPELINE_STAGES.length - 1}
            onClick={() => {
              const newIdx = Math.min(PIPELINE_STAGES.length - 1, mobileStageIndex + 1);
              setMobileStageIndex(newIdx);
              boardRef.current?.children[newIdx]?.scrollIntoView({ behavior: 'smooth', inline: 'start' });
            }}
          >
            <FaChevronRight />
          </MobileNavBtn>
        </MobileNav>

        {/* Pipeline board */}
        <BoardContainer
          ref={boardRef}
          onScroll={() => {
            if (boardRef.current && window.innerWidth <= 768) {
              const scrollLeft = boardRef.current.scrollLeft;
              const colWidth = boardRef.current.children[0]?.offsetWidth || 280;
              const gap = 8;
              const idx = Math.round(scrollLeft / (colWidth + gap));
              setMobileStageIndex(Math.min(idx, PIPELINE_STAGES.length - 1));
            }
          }}
        >
          {PIPELINE_STAGES.map(stage => {
            const stageLeads = getFilteredLeads(pipeline[stage.key]);
            return (
              <Column
                key={stage.key}
                isDragOver={dragOverStage === stage.key}
                stageColor={stage.color}
                onDragOver={e => handleDragOver(e, stage.key)}
                onDragLeave={handleDragLeave}
                onDrop={e => handleDrop(e, stage.key)}
              >
                <ColumnHeader color={stage.color}>
                  <ColumnTitle>
                    <span>{stage.icon}</span> {stage.label}
                  </ColumnTitle>
                  <CountBadge color={stage.color}>{stageLeads.length}</CountBadge>
                </ColumnHeader>

                <CardList>
                  {stageLeads.length === 0 ? (
                    <div style={{
                      textAlign: 'center',
                      padding: '24px 16px',
                      color: '#666',
                      fontSize: 13,
                      opacity: 0.6
                    }}>
                      No leads
                    </div>
                  ) : (
                    stageLeads.map(lead => (
                      <LeadCard
                        key={lead.id}
                        stageColor={stage.color}
                        draggable
                        isDragging={dragData?.lead?.id === lead.id}
                        onDragStart={e => handleDragStart(e, lead, stage.key)}
                        onDragEnd={() => setDragData(null)}
                      >
                        <CardName>
                          <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <FaGripVertical style={{ color: '#666', fontSize: 10 }} />
                            {lead.name}
                          </span>
                          <CardActions>
                            <CallBtn
                              title="Call lead"
                              onClick={() => handleCallClick(lead)}
                            >
                              <FaPhoneAlt />
                            </CallBtn>
                            <CardBtn
                              title="View lead"
                              onClick={() => navigate(`/leads/view/${lead.id}`)}
                            >
                              <FaEye />
                            </CardBtn>
                            <div style={{ position: 'relative' }}>
                              <CardBtn
                                title="Move to..."
                                onClick={() => setMoveMenuLead(moveMenuLead === lead.id ? null : lead.id)}
                              >
                                <FaArrowRight />
                              </CardBtn>
                              {moveMenuLead === lead.id && (
                                <MoveDropdown>
                                  {PIPELINE_STAGES.map(s => (
                                    <MoveOption
                                      key={s.key}
                                      color={s.color}
                                      active={s.key === stage.key}
                                      onClick={() => handleTouchMoveToStage(lead, stage.key, s.key)}
                                    >
                                      <StageDot color={s.color} />
                                      {s.icon} {s.label}
                                      {s.key === stage.key && ' (current)'}
                                    </MoveOption>
                                  ))}
                                </MoveDropdown>
                              )}
                            </div>
                          </CardActions>
                        </CardName>

                        {((lead.status?.status_name || lead.serviceRequests?.[0]?.LeadStatus?.status_name) || lead.origin?.origin_name) && (
                          <div style={{ marginBottom: 6, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                            {(lead.status?.status_name || lead.serviceRequests?.[0]?.LeadStatus?.status_name) && (
                              <LeadStatusBadge color={lead.status?.color || lead.serviceRequests?.[0]?.LeadStatus?.color}>
                                {lead.status?.status_name || lead.serviceRequests?.[0]?.LeadStatus?.status_name}
                              </LeadStatusBadge>
                            )}
                            {lead.origin?.origin_name && (
                              <OriginBadge origin={lead.origin} size="sm" />
                            )}
                          </div>
                        )}

                        <CardInfo>
                          <FaPhone color={stage.color} /> {formatPhone(lead.phone)}
                        </CardInfo>

                        {lead.email && (
                          <CardInfo>
                            <FaEnvelope color={stage.color} /> {lead.email}
                          </CardInfo>
                        )}

                        <CardInfo>
                          <FaMapMarkerAlt color={stage.color} /> {lead.city}, {lead.state}
                        </CardInfo>

                        {lead.estimates?.length > 0 && (
                          <CardInfo>
                            <span style={{ fontWeight: 600, color: stage.color }}>
                              ${lead.estimates.reduce((sum, e) => sum + (parseFloat(e.totalPrice) || 0), 0).toLocaleString()}
                            </span>
                            <span style={{ opacity: 0.7 }}>
                              ({lead.estimates.length} estimate{lead.estimates.length !== 1 ? 's' : ''})
                            </span>
                          </CardInfo>
                        )}

                        <CardDate>
                          <span>
                            <FaCalendarAlt style={{ marginRight: 4, fontSize: 10 }} />
                            {formatStageDate(lead, stage.key) || new Date(lead.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                          </span>
                          <DaysInStage days={getDaysInStage(lead, stage.key)}>
                            {getDaysInStage(lead, stage.key)}d in stage
                          </DaysInStage>
                        </CardDate>
                      </LeadCard>
                    ))
                  )}
                </CardList>
              </Column>
            );
          })}
        </BoardContainer>
      </PageContainer>

      {/* Close move menu on outside click */}
      {moveMenuLead && (
        <div
          style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 99 }}
          onClick={() => setMoveMenuLead(null)}
        />
      )}

      {callModalOpen && leadToCall && (
        <CallModal
          isOpen={callModalOpen}
          onClose={() => {
            setCallModalOpen(false);
            setLeadToCall(null);
          }}
          customerPhone={leadToCall.phone}
          customerName={leadToCall.name}
          leadId={leadToCall.id}
          onCallSuccess={handleCallSuccess}
        />
      )}
    </DashboardLayout>
  );
};

export default LeadPipeline;
